import { useLayoutEffect, useRef, useState } from 'react'

const TAB_GAP = 24
const MORE_RESERVE = 96

export interface OverflowTabs<T> {
    containerRef: (node: unknown) => void
    measureRef: (node: unknown) => void
    visible: T[]
    overflow: T[]
}

/**
 * Splits a horizontal tab row into the tabs that fit and the ones that spill into
 * a "More" menu. The caller renders an off-screen mirror row of the same labels
 * behind `measureRef`; widths are read from it because the live row is already
 * clipped by the time it is too narrow to measure.
 *
 * `MORE_RESERVE` is withheld from every slot but the last so the "More" trigger
 * itself never becomes the item that overflows. An active tab that would land in
 * the overflow is promoted into the last visible slot, so the selected tab is
 * always on screen.
 *
 * The container and every mirror label are observed, not the window: the labels
 * carry a language switch (the same tabs, wider Estonian text) and the container
 * carries the resizes a window listener misses (sidebar toggle, splitter drag).
 */
export const useOverflowTabs = <T>(items: T[], active?: T): OverflowTabs<T> => {
    const containerNode = useRef<HTMLElement | null>(null)
    const measureNode = useRef<HTMLElement | null>(null)
    const [visibleCount, setVisibleCount] = useState(-1)

    useLayoutEffect(() => {
        const container = containerNode.current
        const measure = measureNode.current
        if (!container || !measure) {
            return undefined
        }
        const recompute = () => {
            const available = container.clientWidth
            const widths = Array.from(measure.children).map(c => (c as HTMLElement).offsetWidth)
            let used = 0
            let count = 0
            for (let i = 0; i < widths.length; i++) {
                const next = widths[i] + (i > 0 ? TAB_GAP : 0)
                const reserve = i < widths.length - 1 ? MORE_RESERVE : 0
                if (used + next + reserve <= available) {
                    used += next
                    count++
                } else {
                    break
                }
            }
            setVisibleCount(count)
        }
        recompute()
        const observer = new ResizeObserver(recompute)
        observer.observe(container)
        Array.from(measure.children).forEach(child => observer.observe(child))
        return () => observer.disconnect()
    }, [items])

    const count = visibleCount < 0 ? items.length : visibleCount
    const { visible, overflow } = splitOverflowTabs(items, count, active)

    return {
        containerRef: node => {
            containerNode.current = node as HTMLElement | null
        },
        measureRef: node => {
            measureNode.current = node as HTMLElement | null
        },
        visible,
        overflow,
    }
}

export const splitOverflowTabs = <T>(items: T[], count: number, active?: T) => {
    const visible = items.slice(0, count)
    if (active === undefined || visible.includes(active)) {
        return { visible, overflow: items.slice(count) }
    }
    const promoted = [...items.slice(0, Math.max(0, count - 1)), active]
    return { visible: promoted, overflow: items.filter(item => !promoted.includes(item)) }
}
